import React from "react";
import { useCompany } from "@/context/CompanyContext";
import Seo from "@/components/Seo";
import { SITE_URL } from "@/lib/api";
import Breadcrumbs from "@/components/Breadcrumbs";
import CtaBlock from "@/components/CtaBlock";
import { Rocket, Target, Heart, Users, MapPin, CheckCircle2 } from "lucide-react";

export default function About() {
  const { company } = useCompany();
  const values = [
    { i: Target, t: "Precision", d: "Like the launches next door, we don't cut corners. Every job is done right." },
    { i: Heart, t: "Honesty", d: "Upfront pricing and straight answers — we treat your home like our own." },
    { i: Users, t: "Local Roots", d: "Born and based on the Space Coast, invested in our community." },
  ];
  return (
    <>
      <Seo title="About Aquila Air & Heating | Space Coast HVAC Company" description="Learn about Aquila Air & Heating — your locally owned Space Coast HVAC company delivering honest, mission-grade air conditioning and heating service in Brevard County." path="/about-us/" image="https://images.unsplash.com/photo-1457364887197-9150188c107b?crop=entropy&cs=srgb&fm=jpg&q=85" jsonLd={[{ "@context": "https://schema.org", "@type": "AboutPage", name: "About Aquila Air & Heating", url: `${SITE_URL}/about-us/`, description: "Locally owned Space Coast HVAC company serving Brevard County, FL.", about: { "@type": "HVACBusiness", name: company.name, telephone: company.phone, url: SITE_URL } }]} />
      <section className="relative bg-navy text-white">
        <div className="absolute inset-0"><img src="https://images.unsplash.com/photo-1457364887197-9150188c107b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Rocket launch at dusk over Cape Canaveral on Florida's Space Coast" className="w-full h-full object-cover opacity-30" loading="eager" /><div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/95 to-navy/60" /></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 md:pb-20">
          <div className="[&_*]:!text-slate-300"><Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "About" }]} /></div>
          <p className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-launch"><Rocket className="h-4 w-4" /> Comfort Ready For Liftoff</p>
          <h1 className="mt-3 max-w-3xl text-4xl sm:text-5xl font-extrabold tracking-tight">Your Local Space Coast HVAC Experts</h1>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-navy">Our Story</h2>
          <p className="mt-4 text-lg text-slate-700 leading-relaxed">{company.about_story}</p>
          <p className="mt-4 text-slate-700 leading-relaxed">We started Aquila Air &amp; Heating because Space Coast homeowners deserve an HVAC company that shows up fast, tells the truth, and does the job right the first time. No pressure, no gimmicks — just dependable comfort from neighbors you can trust.</p>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-14">
          <h2 className="text-2xl sm:text-3xl font-bold text-navy text-center">Our Values</h2>
          <div className="mt-8 grid sm:grid-cols-3 gap-6">
            {values.map((v, i) => {
              const Icon = v.i;
              return (
                <div key={i} className="bg-white border border-slate-200 rounded-lg p-6 text-center" data-testid={`value-${i}`}>
                  <span className="inline-flex items-center justify-center h-12 w-12 rounded-md bg-cool/10 text-cool mx-auto"><Icon className="h-6 w-6" /></span>
                  <h3 className="mt-4 text-xl font-semibold text-navy">{v.t}</h3>
                  <p className="mt-2 text-sm text-slate-600">{v.d}</p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-14">
          <div className="bg-navy text-white rounded-xl p-8 dot-grid grid sm:grid-cols-3 gap-6 text-center">
            {[{ n: company.years_experience || "15+", l: "Years of Experience" }, { n: "24/7", l: "Emergency Service" }, { n: (company.counties || []).length || 3, l: "Counties Served" }].map((s, i) => (
              <div key={i}><div className="text-4xl font-extrabold text-launch">{s.n}</div><div className="mt-1 text-slate-200">{s.l}</div></div>
            ))}
          </div>
          <div className="mt-8 flex items-start gap-3 text-slate-600 max-w-2xl mx-auto">
            <MapPin className="h-5 w-5 text-launch flex-shrink-0 mt-0.5" />
            <p>Proudly serving {company.primary_service_area} including {(company.counties || []).join(", ")}. {company.license}.</p>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-white" data-testid="about-owner">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <img src="/about-owner.jpg"
              alt="Todd Keller, owner of Aquila Air & Heating, at his Cocoa, Florida office"
              className="rounded-2xl shadow-lg w-full object-cover" width="1448" height="1086" loading="lazy" />
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.15em] text-launch">About the Owner</p>
              <h2 className="mt-2 text-3xl sm:text-4xl font-extrabold text-navy">Todd Keller</h2>
              <p className="mt-1 text-slate-500 font-medium">A lifelong Cocoa resident. Business leader. HVAC professional. Neighbor.</p>
              <div className="mt-4 space-y-4 text-slate-700 leading-relaxed">
                <p>For Todd Keller, Brevard County isn't just a service area — <strong>it's home.</strong> A lifelong Cocoa resident, Todd attended St. Mary Catholic School in Rockledge (K–8), graduated from Cocoa High School, and earned a Bachelor's in Business Management from Keiser University.</p>
                <p>He started at 16 as a cashier at Winn-Dixie and built a 29-year career through cashier, regional trainer, store manager, and acquisition team lead roles — learning how to listen, solve problems, build strong teams, and lead by example. Today he brings that people-first experience to Aquila Air &amp; Heating.</p>
                <p>That vision led to a powerful differentiator: <strong>Florida's First HVAC Company Offering a Lifetime Warranty</strong> — giving homeowners greater confidence when making one of the biggest investments in their home.</p>
              </div>
            </div>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              ["People Matter", "Take care of customers, employees, and community."],
              ["Show Up", "Do the work. Be accountable. Be there when needed."],
              ["Solve Problems", "Focus on solutions instead of excuses."],
              ["Earn Trust", "Through honesty, consistency, and follow-through."],
              ["Keep Learning", "Experience matters — there's always more to learn."],
            ].map(([t, d]) => (
              <div key={t} className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                <h3 className="font-bold text-navy">{t}</h3>
                <p className="text-sm text-slate-600 mt-1">{d}</p>
              </div>
            ))}
          </div>

          <blockquote className="mt-10 border-l-4 border-launch pl-5 text-lg text-navy font-medium italic">
            "Take care of people. Do honest work. Stand behind what you do." Because when your customers are also your neighbors, your reputation means everything.
          </blockquote>
        </div>
      </section>

      <CtaBlock />
    </>
  );
}
